import { Injectable } from '@angular/core';
import { Hub, Integrations, User, Plan, HubMode, isFreePlan, PlanType, HubUserRole } from '@shared/models';
import * as moment from 'moment';
import { AuthenticationService } from '@shared/services/authentication.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { KalturaService } from '@shared/services/kaltura.service';
import { switchMap } from 'rxjs/operators';
import { IBMService } from '@shared/services/ibm.service';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { User as WondaUser } from '@shared/models/user.model';

@Injectable()
export class CurrentHubService {
  private hubSubject: BehaviorSubject<Hub | null> = new BehaviorSubject(null);
  hub: Hub;
  hubObservable: Observable<Hub> = this.hubSubject.asObservable();
  integrations: Integrations;
  plan: Plan;
  canCreateSpace = true;
  constructor(
    private _authService: AuthenticationService,
    private _kalturaService: KalturaService,
    private _ibmService: IBMService,
    private _courseAPIService: CoursesAPIService
  ) { }

  public async getOrSetCurrentHub(urlTitle: string, forceReload = false): Promise<Hub> {
    if (this.hub?.urlTitle === urlTitle && !forceReload) {
      return this.hub;
    }
    try {
      const hub = await this._courseAPIService.getHub(urlTitle).toPromise();
      await this.setCurrentHub(hub);
      return hub;
    } catch (e) {
      return null;
    }
  }
  public async setDefaultHub(user: WondaUser): Promise<Hub> {
    let newHub: Hub;
    if (user.preferences && user.preferences.defaultHub) {
      newHub = user.Hubs.find((hub) => {
        return hub.id === user.preferences.defaultHub;
      });
    }
    if (newHub === undefined) {
      const lastIndex = user.Hubs.length - 1;
      newHub = user.Hubs[lastIndex];
    }
    return this.setCurrentHub(newHub);
  }
  public async reloadCurrentHub() {
    return this.getOrSetCurrentHub(this.hub.urlTitle, true);
  }

  public async updateHubPlan(id: string) {
    return (this.plan = await this._courseAPIService.getHubPlanInfo(id).toPromise());
  }

  public async refreshCanCreateSpace(): Promise<void> {
    const result = await this._courseAPIService.countLearnspacesInHub(this.hub.id).toPromise();
    this.canCreateSpace = this.plan.maxSpace > result.all;
  }

  public updateCanCreateSpace(nbAll: number): void {
    this.canCreateSpace = this.plan.maxSpace > nbAll;
  }
  public async updateHubIntegrations(hub: Hub) {
    if (this.plan && !this.plan.allowIntegrations) {
      return;
    }
    this.integrations = await this._courseAPIService.getHubIntegrationsInfo(hub.id).toPromise();
    // IBM
    if (this.integrations.ibm) {
      this._ibmService
        .resetConnection(hub.id)
        .pipe(switchMap(() => this._ibmService.checkAccount(hub.id)))
        .subscribe(() => (this.integrations.ibm = true));
    }
    // Kaltura
    if (this.integrations.kaltura) {
      const user = await this._authService.getWondaUser();
      if (!user) { //retry later when the user is more likely to be set -- it is very important that this is non blocking !
        this._authService.userState.subscribe((user: User) => {
          this._kalturaService.startKalturaSession(hub.id, user).subscribe((session) => (this.integrations.kaltura = session));
        });
        return;
      };
      this._kalturaService.startKalturaSession(hub.id, user).subscribe((session) => (this.integrations.kaltura = session));
    }
    //Todo: LTI
    return;
  }
  async setCurrentHub(hub: Hub): Promise<Hub> {
    const hubIdChanged = !this.hub || this.hub.id !== hub.id;
    this.hub = hub;
    if (hubIdChanged) {
      await this.updateHubPlan(hub.id);
      await this.refreshCanCreateSpace();
      await this.updateHubIntegrations(hub);
    }
    this.hubSubject.next(hub);
    return hub;
  }

  public canManageCurrentHub(): boolean {
    if (this._authService.isAdmin()) {
      return true;
    }
    const user = this._authService.wondaUser;
    return user.Hubs.find((hub) => {
      return (hub.id === this.hub.id &&
        (hub.HubsUsers.role === HubUserRole.admin || hub.HubsUsers.role === HubUserRole.manager))
    }) !== undefined;
  }
  public canAdminCurrentHub(): boolean {
    if (this._authService.isAdmin()) {
      return true;
    }
    const user = this._authService.wondaUser;
    return user.Hubs.find((hub) => {
      return (
        hub.id === this.hub.id &&
        (hub.HubsUsers.role === HubUserRole.admin))
    }) !== undefined;
  }

  public planTimeLeft(): number {
    const endDate = moment(this.hub.plan_end_date);
    return Math.round(moment.duration(endDate.diff(moment())).asDays());
  }
  public planExpiration(): Date {
    return this.hub.plan_end_date;
  }
  public isFree(): boolean {
    return isFreePlan(this.hub.plan);
  }
  public isPaid(): boolean {
    return !this.isFree();
  }
  public getHubPlan(): PlanType {
    return this.hub.plan instanceof Plan ? this.hub.plan?.type : this.hub.plan;
  }
  public getHubTitle(): string {
    return this.hub.title;
  }
  public getHubURLTitle(): string {
    return this.hub.urlTitle;
  }
  public getHubMode(): HubMode {
    return this.hub.mode || HubMode.program;
  }
  public isLTIEnabled(): boolean {
    return this.hub.lti_enabled;
  }
  public isVersioningEnabled(): boolean {
    return this.hub.properties.enableVersioning;
  }
  public isTranslationEnabled(): boolean {
    return this.hub.properties.enableTranslation;
  }
  public isLTIReturnURLTypeLinkEnabled(): boolean {
    return this.hub.properties.enableLTIReturnURLTypeLink;
  }
}
